import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

function Contact() {
  const { t } = useTranslation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Contact',
        page_path: window.location.pathname,
      });
    }
  }, []);

  return (
    <div className="contact-page" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-warm-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-4">{t('contact.overline')}</div>
          <h1 className="font-playfair text-5xl md:text-7xl tracking-tighter leading-[1.1] font-medium text-charcoal mb-6" data-testid="contact-title">
            {t('contact.title')}
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-charcoal/80">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Info & Map */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-playfair text-3xl md:text-4xl font-medium text-charcoal mb-8">
                {t('contact.getInTouch')}
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.addressLabel')}</h3>
                    <p className="text-charcoal/80">{t('contact.address')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.phoneLabel')}</h3>
                    <a href="tel:514-272-9016" className="text-charcoal/80 hover:text-primary transition-colors">
                      {t('contact.phone')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.emailLabel')}</h3>
                    <a href="mailto:woof@lecasapooch.com" className="text-charcoal/80 hover:text-primary transition-colors">
                      {t('contact.email')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal mb-1">{t('contact.hours')}</h3>
                    <p className="text-charcoal/80">{t('contact.hoursSchedule')}</p>
                    <p className="text-charcoal/60 text-sm mt-1">{t('contact.closed')}</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(59,48,45,0.08)]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2795.3573842892484!2d-73.63187!3d45.53621!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc919f3b3b3b3b3%3A0x3b3b3b3b3b3b3b3b!2s70%20Rue%20Jean-Talon%20O%2C%20Montr%C3%A9al%2C%20QC%20H2R%202W7!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t('contact.mapTitle')}
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
